import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
} from "@mui/material";

/**
 * DataTableReport - Template for table-based reports
 *
 * @param {string} title - Report title
 * @param {string} description - Report description
 * @param {Array} columns - Table columns config: [{ field: 'name', headerName: 'Name', width: 150 }]
 * @param {Function} dataFetchFn - Function to fetch data, receives filters as param
 * @param {Object} filters - Current filter values
 * @param {boolean} showPagination - Whether to show pagination
 * @param {number} defaultRowsPerPage - Default rows per page
 * @param {Function} customRenderAfterTable - Optional function to render content after the table, receives full data
 */
const DataTableReport = ({
  title,
  description,
  columns = [],
  dataFetchFn,
  filters = {},
  showPagination = true,
  defaultRowsPerPage = 25,
  customRenderAfterTable,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [fullData, setFullData] = useState(null); // Store full data for customRenderAfterTable
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const fetchData = useCallback(async () => {
    if (!dataFetchFn) {
      setData([]);
      setFullData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await dataFetchFn(filters);
      const responseData = response.data || [];

      // If response.data is an array, use it directly
      // If it's an object with tableData, extract tableData for the table
      if (Array.isArray(responseData)) {
        setData(responseData);
        setFullData(responseData);
      } else {
        setData(responseData.tableData || []);
        setFullData(responseData);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load data");
      setData([]);
      setFullData(null);
    } finally {
      setLoading(false);
    }
  }, [dataFetchFn, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = showPagination
    ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : data;

  return (
    <Box>
      {/* Header */}
      {(title || description) && (
        <Box sx={{ mb: 3 }}>
          {title && (
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Data Table */}
      {!loading && !error && (
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "12px",
            boxShadow: theme.shadows[1],
            overflowX: "auto",
            width: "100%",
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    sx={{
                      fontWeight: 700,
                      bgcolor: theme.palette.background.default,
                      width: column.width || "auto",
                    }}
                  >
                    {column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography variant="body2" color="text.secondary" py={4}>
                      No data available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow
                    key={row.id || index}
                    sx={{
                      "&:hover": {
                        bgcolor: theme.palette.action.hover,
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.field}>
                        {column.renderCell
                          ? column.renderCell(row)
                          : row[column.field]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {showPagination && data.length > 0 && (
            <TablePagination
              component="div"
              count={data.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </TableContainer>
      )}

      {/* Custom content after table */}
      {!loading &&
        !error &&
        customRenderAfterTable &&
        customRenderAfterTable(fullData)}
    </Box>
  );
};

export default DataTableReport;
